import type { GridCustomCellProps } from "@tql/ui-components-react/kendo/grid";

import { GridTd } from "~/shared/components/trax-grid/grid-td/grid-td.tsx";
import { LoaderCell } from "~/shared/components/trax-grid/loader-cell/loader-cell.tsx";
import type { Load } from "~/shared/schemas/loads-schema.ts";

type LocationKind = "pickup" | "delivery";

type RenderLocationCellArgs = {
	isLoading: boolean;
	kind: LocationKind;
};

const formatCityStateZip = (
	city: string | null | undefined,
	state: string | null | undefined,
	zip: string | null | undefined,
): string => {
	const cityState = [city, state].filter(Boolean).join(", ");
	return [cityState, zip].filter(Boolean).join(" ");
};

/**
 * Returns a Kendo `cells.data` renderer for the Pickup/Delivery Location
 * columns. Renders the location name in bold on its own line while keeping
 * "City, ST ZIP" on a separate line in normal weight.
 */
export const buildLocationCell = ({
	isLoading,
	kind,
}: RenderLocationCellArgs) => {
	return function LocationCell(props: GridCustomCellProps) {
		if (isLoading) return <LoaderCell {...props} />;

		const load = props.dataItem as Load | undefined;
		if (!load) {
			return <GridTd tdProps={props.tdProps}></GridTd>;
		}

		const name = kind === "pickup" ? load.firstPickName : load.lastDropName;
		const cityStateZip =
			kind === "pickup"
				? formatCityStateZip(
						load.firstPickCity,
						load.firstPickState,
						load.firstPickZIP,
					)
				: formatCityStateZip(
						load.lastDropCity,
						load.lastDropState,
						load.lastDropZIP,
					);

		return (
			<GridTd tdProps={props.tdProps}>
				{name ? (
					<>
						<strong>{name}</strong>
						{cityStateZip ? <br /> : null}
					</>
				) : null}
				{cityStateZip}
			</GridTd>
		);
	};
};
